// AppCelularGate.jsx — pop-up B1 "Lleva SmartDx a tu celular" (#1686, fase 2a).
//
// Cubre R25-R30, R32-R34, R41 — .claude/specs/1686-checklist-uso-celular/
//
// Sale en el COMPUTADOR a quien ya usa bien la app y nunca la abrió desde el celular. El backend decide
// (`/me` → `app_celular.mostrar`); acá solo se suma el aparato, la ruta y que no haya otro overlay
// (`helpers/app-celular.js` `decidirPopupCelular`). Bloqueante como los Términos pero con salida: solo
// "Ahora no" y "Ya la tengo en el celular" lo cierran (Escape y el clic fuera no).
//
// - A mano (portal + Box) y no un Dialog de MUI, como `InstallAppGuide` y `TosAcceptanceGate`: un Dialog
//   de MUI abierto bajo otro overlay roba el foco (#1534) y el z-index se controla acá. z 19000: sobre
//   el tutorial (15500) y bajo Términos y perfil (20000).
// - Fail-open (R29, R30): el pop-up se cierra al tocar, ANTES de que conteste el servidor, y no vuelve
//   en esta carga aunque el POST falle. Un backend caído nunca deja a nadie atrapado.
// - D14, R27 (lo que el código garantiza, nada más):
//   * En /turno el pop-up NO se abre por tiempo. Espera la señal `helpers/turno-arranque.js`, que publica
//     `Turno.js` cuando su effect de unidad (lugares y pestañas ya cargados) terminó. Con "sin_dialogo"
//     se abre, tras ESPERA_MS y una última mirada al DOM. Con "con_dialogo" (selector "¿Dónde trabajas
//     hoy?", formulario de primera unidad o censo pendiente) no sale en esa carga. Si la señal nunca
//     llega (un pedido colgado sin respuesta, intro o tutorial abiertos, superuser mirando a otro)
//     tampoco sale: falla hacia el lado seguro. Un error de red no deja la señal colgada (el `finally`
//     de `Turno.js` marca lugares y pestañas como cargados y el effect decide con lo que haya): si se
//     cae la carga de lugares sin censo pendiente, el Turno decide "sin_dialogo" y el pop-up puede
//     salir; con pestañas caídas (`loadTabs`) y unidades sin caché de hoy el Turno abre el selector
//     ("con_dialogo") y el pop-up no sale en esa carga, igual con lugares caídos y un censo pendiente.
//     En ningún caso se apilan dos diálogos.
//     Revisor 2a v2 (F1): con una ventana fija de 1 s y lugares lentos el selector se
//     abría debajo del pop-up y el FocusTrap de MUI le robaba el foco (#1534).
//   * En / y /home no hay Turno ni señal: se mira el DOM durante ESPERA_MS y otra vez al abrirse.
//   * Marcas pegajosas por carga: si hubo un gate de alta, `reintro_pendiente` (el rescate de
//     inactivos), el tutorial de camas pendiente, un tutorial u otro diálogo en el DOM, o la señal
//     "con_dialogo", no sale hasta la próxima carga.
//   * Límite conocido (O-b): con `reintro_pendiente` en true no sale en ninguna carga hasta que la
//     persona cierre la intro del Turno (ahí el backend apaga la marca).
//   * Límite conocido: si después de abrirse el pop-up se abre un diálogo AJENO al arranque del Turno
//     (por ejemplo un 429 en vivo), el intervalo de 400 ms sigue corriendo: el pop-up se retira en
//     ≤400 ms y no vuelve en esa carga. Los dos pueden coincidir hasta ese tick.
//   * Límite conocido (O1): con Atrás y luego Adelante del navegador, al volver a /turno el pop-up
//     puede dibujarse un frame, porque `arranque` aún vale "sin_dialogo" antes del effect de montaje
//     del Turno que lo devuelve a "pendiente". Se retira solo, sin superponerse con el selector.
// FASE 2a, sin WhatsApp: el hueco del botón "Mándamelo por WhatsApp" (con su "o" y el estado "Enviado a")
// queda abajo del QR, sin dibujar. Lo agrega #1686 fase 2b cuando #1683 esté en la base (R31).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useCurrentUser } from "../context/UserContext";
import useHuboGateDeAlta from "../../hooks/useHuboGateDeAlta";
import { esEscritorio } from "../../helpers/idle-session";
import { esComputadorParaTutorial } from "../../helpers/tutorialPrimerDoc";
import { leerArranqueTurno, suscribirArranqueTurno } from "../../helpers/turno-arranque";
import { camasPendiente, decidirPopupCelular, textoContador } from "../../helpers/app-celular";
import { accionAppCelular } from "../../helpers/app-celular-api";
// Con extensión: en un disco sin distinción de mayúsculas "./QrLogin" resolvería a `qrLogin.js` (la matriz).
import QrLogin from "./QrLogin.jsx";

const Z_POPUP = 19000;
// Espera antes de abrirlo solo: que el Turno alcance a decidir si abre un tutorial (R27) y que la
// persona vea dónde cayó. Mismo criterio que `InstallAppGuide`.
const ESPERA_MS = 1000;
// Cualquier otro overlay visible: tutoriales, Dialog/Drawer/Menu de MUI y diálogos a mano (`role="dialog"`),
// menos el propio pop-up. Los tutoriales son capas propias (no MUI): cada uno nuevo se suma acá, o el pop-up
// sale encima (tutorial 3 «Escanea documentos al PREVIO», 29-oct).
const SEL_OTRO_POPUP =
  '[data-testid="tutorial-primer-doc"], [data-testid="tutorial-camas"], [data-testid="tutorial-escanear"], .MuiModal-root, [role="dialog"]:not([data-testid="app-celular-popup"])';
const visible = (el) => {
  if (el.getAttribute("aria-hidden") === "true" && !el.matches(".MuiModal-root")) return false;
  const st = window.getComputedStyle(el);
  return st.visibility !== "hidden" && st.display !== "none";
};
const hayOtroPopupAbierto = () =>
  typeof document !== "undefined" && Array.from(document.querySelectorAll(SEL_OTRO_POPUP)).some(visible);

const boton = {
  font: "inherit",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  borderRadius: "999px",
  border: "1px solid transparent",
  padding: "10px 18px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

const kbd = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  border: "1px solid var(--border-strong)",
  borderRadius: "6px",
  padding: "0 6px",
  fontSize: 12,
  backgroundColor: "var(--bg-input)",
  verticalAlign: "1px",
};

const Paso = ({ n, children }) => (
  <Box component="li" sx={{ display: "flex", gap: "12px", alignItems: "flex-start", py: "7px" }}>
    <Box
      sx={{
        flex: "none",
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: "var(--color-primary)",
        color: "var(--text-on-primary)",
        fontWeight: 700,
        fontSize: 12.5,
        display: "grid",
        placeItems: "center",
      }}
    >
      {n}
    </Box>
    <Box sx={{ pt: "2px" }}>{children}</Box>
  </Box>
);

export function AppCelularPopup({ veces, onAhoraNo, onYaLaTengo }) {
  const cajaRef = useRef(null);

  // Foco dentro del cuadro y atrapado ahí (modal bloqueante). Escape no hace nada, ni siquiera en un
  // diálogo que haya quedado debajo: se corta en captura antes de que llegue a MUI.
  useEffect(() => {
    const prev = document.activeElement;
    // El foco entra al cuadro mismo (tabIndex -1, sin aro) y no a un botón: así no se ve un aro de foco
    // sobre "Ya la tengo" que el mockup no tiene; con Tab el aro sale en el botón que corresponde.
    cajaRef.current?.focus();
    const alTeclear = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key !== "Tab" || !cajaRef.current) return;
      const f = cajaRef.current.querySelectorAll("button, [href], [tabindex]:not([tabindex='-1'])");
      if (!f.length) return;
      const primero = f[0];
      const ultimo = f[f.length - 1];
      if (!cajaRef.current.contains(document.activeElement)) {
        e.preventDefault();
        primero.focus();
      } else if (e.shiftKey && (document.activeElement === primero || document.activeElement === cajaRef.current)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };
    window.addEventListener("keydown", alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", alTeclear, true);
      document.body.style.overflow = overflow;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, []);

  return (
    <Box
      data-testid="app-celular-scrim"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: Z_POPUP,
        display: "grid",
        placeItems: "center",
        p: "20px",
        backgroundColor: "rgba(var(--shadow-rgb), 0.65)",
      }}
    >
      <Box
        ref={cajaRef}
        role="dialog"
        aria-modal="true"
        aria-label="Lleva SmartDx a tu celular"
        data-testid="app-celular-popup"
        tabIndex={-1}
        sx={{
          outline: "none",
          width: "100%",
          maxWidth: 720,
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "230px 1fr" },
          backgroundColor: "var(--bg-elevated)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-strong)",
          borderRadius: "16px",
          boxShadow: "0 24px 60px rgba(var(--shadow-rgb), 0.5)",
          fontSize: 14,
          lineHeight: 1.45,
        }}
      >
        <Box
          sx={{
            p: "26px 22px",
            borderRight: { md: "1px solid var(--border-soft)" },
            borderBottom: { xs: "1px solid var(--border-soft)", md: "none" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            textAlign: "center",
            backgroundColor: "rgba(var(--color-primary-rgb), 0.05)",
            borderRadius: { xs: "16px 16px 0 0", md: "16px 0 0 16px" },
          }}
        >
          {/* La caja mide 170 como la del mockup (150 + 10 de borde a borde). El QR real trae su propia zona
              de silencio de 4 módulos adentro: por eso no lleva otro relleno encima. */}
          <Box
            sx={{
              backgroundColor: "var(--qr-bg)",
              borderRadius: "12px",
              overflow: "hidden",
              display: "inline-block",
              lineHeight: 0,
              border: "1px solid var(--border-strong)",
            }}
          >
            <QrLogin size={168} />
          </Box>
          <Box sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>Apunta la cámara del celular a este código</Box>
          {/* Fase 2b (#1686, cuando #1683 esté en la base): acá van el "o", el botón
              `app-celular-whatsapp` y su estado "Enviado a …" (R31). Sin dibujar en la fase 2a. */}
        </Box>

        <Box sx={{ p: "24px 26px" }}>
          <Box component="h3" sx={{ m: "0 0 6px", fontSize: 20, fontWeight: 700 }}>
            Lleva SmartDx a tu celular
          </Box>
          <Box component="p" sx={{ color: "var(--text-muted)", m: "0 0 14px" }}>
            Ya la usas bien en el computador. En el celular la tienes a mano durante el turno, para anotar en
            las camas y ver a tus pacientes.
          </Box>
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, color: "var(--text-secondary)" }}>
            <Paso n={1}>
              Abre el link en el celular, <b style={{ fontWeight: 600 }}>en Safari</b>. Se abre la pantalla de
              iniciar sesión.
            </Paso>
            <Paso n={2}>
              Ahí, antes de entrar, toca{" "}
              <Box component="span" sx={kbd}>
                <IosShareIcon sx={{ fontSize: 12 }} /> Compartir
              </Box>{" "}
              (si no está, dentro de ⋯) y luego <b style={{ fontWeight: 600 }}>"Agregar a inicio"</b>.
            </Paso>
            <Paso n={3}>
              Abre SmartDx <b style={{ fontWeight: 600 }}>desde el ícono</b> e inicia sesión una vez. Queda lista
              para siempre.
            </Paso>
          </Box>
          <Box component="p" sx={{ fontSize: 12, color: "var(--text-faint)", m: "10px 0 0" }}>
            En Android: Chrome, menú ⋮ y "Agregar a pantalla principal".
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              mt: "16px",
              pt: "14px",
              borderTop: "1px solid var(--border-soft)",
              flexWrap: "wrap",
            }}
          >
            <Box
              component="span"
              data-testid="app-celular-contador"
              data-n={veces}
              sx={{ fontSize: 11.5, color: "var(--text-faint)" }}
            >
              {textoContador(veces)}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Box
                component="button"
                type="button"
                data-testid="app-celular-ahora-no"
                onClick={onAhoraNo}
                sx={{ ...boton, background: "transparent", color: "var(--text-muted)", padding: "10px 10px" }}
              >
                Ahora no
              </Box>
              <Box
                component="button"
                type="button"
                data-testid="app-celular-ya-la-tengo"
                onClick={onYaLaTengo}
                sx={{ ...boton, backgroundColor: "var(--color-primary)", color: "var(--text-on-primary)" }}
              >
                Ya la tengo en el celular
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function AppCelularGate() {
  const { currentUser, loading, refreshUser } = useCurrentUser() || {};
  const { pathname } = useLocation();
  const [cerrado, setCerrado] = useState(false);
  const [listo, setListo] = useState(false);
  const [otroAhora, setOtroAhora] = useState(false);
  // Marcas pegajosas de ESTA carga (R27, D14): una vez que hubo otro overlay o tutorial, no sale hasta
  // la próxima carga, ni cuando el otro se cierre.
  const huboGate = useHuboGateDeAlta(currentUser);
  const vioOtro = useRef(false);
  const vioCamas = useRef(false);
  const vioReintro = useRef(false);
  const vioArranqueConDialogo = useRef(false);
  // Señal de `Turno.js` (`helpers/turno-arranque.js`). Se lee en cada render y se re-renderiza al llegar.
  const [arranque, setArranque] = useState(leerArranqueTurno());
  useEffect(() => {
    setArranque(leerArranqueTurno());
    return suscribirArranqueTurno(() => setArranque(leerArranqueTurno()));
  }, []);
  if (arranque === "con_dialogo") vioArranqueConDialogo.current = true;
  if (camasPendiente(currentUser)) vioCamas.current = true;
  if (otroAhora) vioOtro.current = true;
  // Rescate de inactivos: ReintroGate manda al wizard y "Completar después" vuelve a /turno en la misma
  // carga, sin ningún overlay de alta que `huboGate` pueda ver (O5 del revisor).
  if (currentUser?.reintro_pendiente === true) vioReintro.current = true;

  // El aparato no cambia durante la carga: se mide una vez (mismo criterio que el tutorial del primer
  // computador de Turno.js; el iPad cuenta como celular, R26).
  const computador = useMemo(
    () =>
      typeof navigator !== "undefined" &&
      esComputadorParaTutorial({
        escritorio: esEscritorio(),
        ua: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        platform: navigator.platform,
      }),
    [],
  );

  const quiere =
    !loading &&
    decidirPopupCelular({
      user: currentUser,
      computador,
      ruta: pathname,
      gateDeAlta: huboGate,
      tutorialAbierto:
        vioOtro.current || vioCamas.current || vioReintro.current || vioArranqueConDialogo.current || otroAhora,
      cerradoEnEstaCarga: cerrado,
    });

  // Mirar el DOM: los tutoriales y diálogos viven en Turno y no comparten estado con este gate.
  useEffect(() => {
    if (!quiere && !listo) return undefined;
    setOtroAhora(hayOtroPopupAbierto());
    const id = window.setInterval(() => setOtroAhora(hayOtroPopupAbierto()), 400);
    return () => window.clearInterval(id);
  }, [quiere, listo]);

  // En /turno la espera arranca recién con la señal "sin_dialogo" (F1). Fuera de /turno no hay señal.
  const arranqueResuelto = pathname !== "/turno" || arranque === "sin_dialogo";
  useEffect(() => {
    if (!quiere || !arranqueResuelto) return undefined;
    // Al cumplirse la espera se mira el DOM una vez más, sin esperar al intervalo: si el Turno abrió
    // un diálogo en el último instante, este pop-up no se abre encima (D14).
    const t = window.setTimeout(() => {
      if (hayOtroPopupAbierto()) {
        setOtroAhora(true);
        return;
      }
      setListo(true);
    }, ESPERA_MS);
    return () => window.clearTimeout(t);
  }, [quiere, arranqueResuelto]);

  const cerrarCon = useCallback(
    (accion) => {
      // Fail-open: cierra YA y no vuelve en esta carga; el POST es de fondo (R29, R30).
      setCerrado(true);
      Promise.resolve(accionAppCelular(accion))
        .then(() => refreshUser?.())
        .catch(() => {});
    },
    [refreshUser],
  );

  // Marcador para prod (el literal sobrevive al minificador). Oculto: no es UI.
  if (!quiere) return null;
  if (!listo || !arranqueResuelto) return <span data-testid="app-celular-pendiente" hidden />;

  return createPortal(
    <AppCelularPopup
      veces={Number.isInteger(currentUser.app_celular.veces_restantes) ? currentUser.app_celular.veces_restantes : 0}
      onAhoraNo={() => cerrarCon("ahora_no")}
      onYaLaTengo={() => cerrarCon("ya_la_tengo")}
    />,
    document.body,
  );
}
